import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';

type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

interface PublishedEnvelope {
  v: 1;
  d: JsonValue;
}

export interface PublishedDecodeResult {
  json: string;
  checksum: string;
  payload: string;
  name?: string;
}

function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function compactJson(value: JsonValue): JsonValue | undefined {
  if (Array.isArray(value)) {
    const compacted = value
      .map((item) => compactJson(item))
      .filter((item): item is JsonValue => item !== undefined);
    return compacted.length > 0 ? compacted : undefined;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value)
      .map(([key, inner]) => [key, compactJson(inner as JsonValue)] as const)
      .filter(([, inner]) => inner !== undefined);
    if (entries.length === 0) return undefined;
    return Object.fromEntries(entries) as JsonValue;
  }
  if (value === null) return undefined;
  return value;
}

function parseHashPayload(hash: string): { payload: string; checksum: string } | null {
  const normalized = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!normalized.startsWith('json=')) return null;
  const encoded = normalized.slice(5);
  if (!encoded) return null;
  const commaIndex = encoded.indexOf(',');
  if (commaIndex === -1) return { payload: encoded, checksum: '' };
  return {
    payload: encoded.slice(0, commaIndex),
    checksum: encoded.slice(commaIndex + 1),
  };
}

export function buildPublishedHash(rawDiagramJson: string): { hash: string; checksum: string; payload: string } {
  const parsed = JSON.parse(rawDiagramJson) as JsonValue;
  const compact = compactJson(parsed) ?? {};
  const envelope: PublishedEnvelope = { v: 1, d: compact };
  const minified = JSON.stringify(envelope);
  const payload = compressToEncodedURIComponent(minified);
  const checksum = fnv1a(minified);
  return {
    hash: `#json=${payload},${checksum}`,
    checksum,
    payload,
  };
}

export function decodePublishedHash(hash: string): PublishedDecodeResult | null {
  const parsed = parseHashPayload(hash);
  if (!parsed) return null;
  const decompressed = decompressFromEncodedURIComponent(parsed.payload);
  if (!decompressed) return null;
  const computedChecksum = fnv1a(decompressed);
  if (parsed.checksum && parsed.checksum !== computedChecksum) return null;

  const envelopeOrData = JSON.parse(decompressed) as PublishedEnvelope | Record<string, unknown>;
  const data = (envelopeOrData as PublishedEnvelope).v === 1
    ? (envelopeOrData as PublishedEnvelope).d
    : (envelopeOrData as JsonValue);
  const name = typeof data === 'object' && data !== null && !Array.isArray(data) && typeof data.name === 'string'
    ? data.name
    : undefined;
  return {
    json: JSON.stringify(data),
    checksum: computedChecksum,
    payload: parsed.payload,
    name,
  };
}
